import { useCallback } from 'react';
import { commitLocalUpdate, useRelayEnvironment } from 'react-relay';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import fireErrorAnalytics from '@atlassian/error-handling/fire-error-analytics';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import RelayDataID from '@atlassian/relay-data-id';
import { updateStarredSavedSearch, deleteStarredSavedSearch } from '../endpoints/index.tsx';
import type { AlertsMenuItemContentNew$data } from '../../ui/alerts-content/alerts-menu-item/__generated__/AlertsMenuItemContentNew.graphql.ts';

const starSavedSearchError = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations-service-use-starred-search.star-saved-search-error',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Failed to star saved search',
	description: 'Error message when failing to star a saved search',
});

const unstarSavedSearchError = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations-service-use-starred-search.unstar-saved-search-error',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Failed to unstar saved search',
	description: 'Error message when failing to unstar a saved search',
});

export const useStarredSearchService = (): {
	starSavedSearch: (
		savedSearch: NonNullable<
			NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
		>['createdByMe'][number],
	) => Promise<void>;
	unstarSavedSearch: (
		savedSearch: NonNullable<
			NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
		>['createdByMe'][number],
	) => Promise<void>;
	removeStarredSearchFromCache: (searchId: string) => void;
} => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getSearchIdFromAriOrPredefined = (search: string): string => {
		const searchId = OpsgenieSavedSearchAri.parse(search).savedSearchId;

		if (!searchId) {
			throw new Error('Invalid saved search ID');
		}

		return searchId;
	};

	const removeStarredSearchFromCache = useCallback(
		(searchId: string): void => {
			commitLocalUpdate(environment, (store) => {
				const root = store.getRoot();
				const opsgenie = root.getLinkedRecord('opsgenie');
				if (opsgenie) {
					const savedSearches = opsgenie.getLinkedRecord('savedSearches', {
						cloudId,
					});
					if (savedSearches) {
						const starred = savedSearches.getLinkedRecords('starred') || [];
						const newStarred = starred.filter((item) => item?.getValue('id') !== searchId);
						savedSearches.setLinkedRecords(newStarred, 'starred');
					}
				}
			});
		},
		[environment, cloudId],
	);

	const starSavedSearch = useCallback(
		async (
			savedSearch: NonNullable<
				NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
			>['createdByMe'][number],
		): Promise<void> => {
			// oxlint-disable-next-line react/react-compiler
			await updateStarredSavedSearch(getSearchIdFromAriOrPredefined(savedSearch.id))
				.then(() => {
					commitLocalUpdate(environment, (store) => {
						const root = store.getRoot();

						const opsgenie = root.getLinkedRecord('opsgenie');

						if (opsgenie) {
							const savedSearches = opsgenie.getLinkedRecord('savedSearches', {
								cloudId,
							});

							if (savedSearches) {
								const savedSearchId = RelayDataID({ id: savedSearch.id }, 'OpsgenieSavedSearch');
								if (!savedSearchId) {
									throw new Error('Saved search ID not found');
								}

								const existingSavedSearch = store.get(savedSearchId);
								if (existingSavedSearch) {
									const starred = savedSearches.getLinkedRecords('starred') ?? [];
									savedSearches.setLinkedRecords([...starred, existingSavedSearch], 'starred');
								} else {
									throw new Error('Saved search not found');
								}
							}
						}
						fireTrackAnalytics(createAnalyticsEvent({}), 'savedSearch starred', {
							opsgenieSavedSearchId: savedSearch.id,
						});
					});
				})
				.catch((error) => {
					fireErrorAnalytics({
						meta: {
							id: 'starSavedSearch',
							packageName: 'jiraNavigationAppsSidebarNav4SidebarsContentOperations',
							teamName: 'Alexstrasza',
						},
						error,
					});
					showFlag({
						type: 'error',
						title: formatMessage(starSavedSearchError),
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId:
							'navigation-apps-sidebar-nav4-sidebars-content-operations.service.use-starred-search.show-flag.error.star-saved-search',
						messageType: 'transactional',
					});
				});
		},
		[environment, createAnalyticsEvent, cloudId, showFlag, formatMessage],
	);

	const unstarSavedSearch = useCallback(
		async (
			savedSearch: NonNullable<
				NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
			>['createdByMe'][number],
		): Promise<void> => {
			// oxlint-disable-next-line react/react-compiler
			await deleteStarredSavedSearch(getSearchIdFromAriOrPredefined(savedSearch.id))
				.then(() => {
					removeStarredSearchFromCache(savedSearch.id);
					fireTrackAnalytics(createAnalyticsEvent({}), 'savedSearch unstarred', {
						opsgenieSavedSearchId: savedSearch.id,
					});
				})
				.catch((error) => {
					fireErrorAnalytics({
						meta: {
							id: 'unstarSavedSearch',
							packageName: 'jiraNavigationAppsSidebarNav4SidebarsContentOperations',
							teamName: 'Alexstrasza',
						},
						error,
					});
					showFlag({
						type: 'error',
						title: formatMessage(unstarSavedSearchError),
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId:
							'navigation-apps-sidebar-nav4-sidebars-content-operations.service.use-starred-search.show-flag.error.unstar-saved-search',
						messageType: 'transactional',
					});
				});
		},
		[createAnalyticsEvent, formatMessage, showFlag, removeStarredSearchFromCache],
	);

	return {
		starSavedSearch,
		unstarSavedSearch,
		removeStarredSearchFromCache,
	};
};
